import { useState, useEffect } from 'react';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';

interface ApiKey {
  id: string;
  name: string;
  permissions: 'read' | 'write' | 'full';
  isActive: boolean;
  createdAt: string;
  lastUsed: string | null;
  expiresAt: string | null;
  keyPreview: string;
}

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<'read' | 'write' | 'full'>('read');
  const [newKeyExpiresIn, setNewKeyExpiresIn] = useState<string>('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);

  const loadApiKeys = async () => {
    try {
      const data = await api.get<{ success: boolean; keys: ApiKey[] }>('/api-keys');
      if (data.success) {
        setApiKeys(data.keys);
      }
    } catch (error) {
      toast.error('Error al cargar API keys');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      const data = await api.post<{ success: boolean; key: { id: string; name: string; key: string; permissions: string; expiresAt: string | null } }>('/api-keys', {
        name: newKeyName,
        permissions: newKeyPermissions,
        expiresIn: newKeyExpiresIn ? parseInt(newKeyExpiresIn) : undefined,
      });

      if (data.success) {
        setNewlyCreatedKey(data.key.key);
        setShowNewKey(true);
        setNewKeyName('');
        setNewKeyPermissions('read');
        setNewKeyExpiresIn('');
        loadApiKeys();
        toast.success('API Key creada exitosamente');
      }
    } catch (error) {
      toast.error('Error al crear API key');
    }
  };

  const deleteApiKey = async (id: string) => {
    try {
      await api.delete(`/api-keys/${id}`);
      setApiKeys(apiKeys.filter(k => k.id !== id));
      toast.success('API Key eliminada');
    } catch (error) {
      toast.error('Error al eliminar API key');
    }
  };

  const toggleApiKey = async (id: string) => {
    try {
      const data = await api.put<{ success: boolean; isActive: boolean }>(`/api-keys/${id}/toggle`);
      if (data.success) {
        setApiKeys(apiKeys.map(k => k.id === id ? { ...k, isActive: data.isActive } : k));
        toast.success(data.isActive ? 'API Key activada' : 'API Key desactivada');
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleDateString('es-ES');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">API Keys</h1>
        <p className="text-muted-foreground">
          Gestiona tus claves de API para acceder a los recursos desde aplicaciones externas.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Crear nueva API Key
          </CardTitle>
          <CardDescription>
            Crea una nueva clave de API para acceder a tus documentos, tareas y proyectos desde aplicaciones externas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Nombre</label>
              <Input
                placeholder="Ej: Mi aplicación móvil"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
            <div className="w-40">
              <label className="text-sm font-medium mb-2 block">Permisos</label>
              <Select value={newKeyPermissions} onValueChange={(v: 'read' | 'write' | 'full') => setNewKeyPermissions(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Solo lectura</SelectItem>
                  <SelectItem value="write">Lectura y escritura</SelectItem>
                  <SelectItem value="full">Full access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <label className="text-sm font-medium mb-2 block">Expira (días)</label>
              <Input
                type="number"
                placeholder="Opcional"
                value={newKeyExpiresIn}
                onChange={(e) => setNewKeyExpiresIn(e.target.value)}
              />
            </div>
            <Button onClick={createApiKey}>
              <Plus className="w-4 h-4 mr-2" />
              Crear
            </Button>
          </div>
        </CardContent>
      </Card>

      {newlyCreatedKey && showNewKey && (
        <Card className="mb-6 border-yellow-500">
          <CardHeader>
            <CardTitle className="text-yellow-600">¡Guarda tu API Key!</CardTitle>
            <CardDescription>
              Esta es la única vez que podrás ver la clave completa. Guárdala en un lugar seguro.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={newlyCreatedKey}
                readOnly
                type={showNewKey ? 'text' : 'password'}
                className="font-mono"
              />
              <Button variant="outline" onClick={() => setShowNewKey(!showNewKey)}>
                {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button variant="outline" onClick={() => copyToClipboard(newlyCreatedKey)}>
                <Copy className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setShowNewKey(false)}>
                Cerrar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Tus API Keys</CardTitle>
          <CardDescription>
            {apiKeys.length} {apiKeys.length === 1 ? 'clave' : 'claves'} registrada{apiKeys.length !== 1 && 's'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No tienes API keys registradas
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{key.name}</span>
                      <Badge variant={key.isActive ? 'default' : 'secondary'}>
                        {key.isActive ? 'Activa' : 'Inactiva'}
                      </Badge>
                      <Badge variant="outline">{key.permissions}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <code className="bg-muted px-1 rounded">{key.keyPreview}</code>
                      {' • '}
                      Creada: {formatDate(key.createdAt)}
                      {' • '}
                      Último uso: {formatDate(key.lastUsed)}
                      {key.expiresAt && (
                        <>{' • '}Expira: {formatDate(key.expiresAt)}</>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleApiKey(key.id)}
                    >
                      {key.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteApiKey(key.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Documentación de la API</CardTitle>
          <CardDescription>
            Usa tu API Key para acceder a los endpoints REST desde aplicaciones externas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Autenticación</h4>
              <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
                {`curl -H "X-API-Key: mky_xxxxxxxxxxxxxxxx" https://api.memories.app/api/v1/documents`}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2">Endpoints disponibles</h4>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li><code>GET /api/v1/documents</code> - Listar documentos</li>
                <li><code>POST /api/v1/documents</code> - Crear documento</li>
                <li><code>PUT /api/v1/documents/:id</code> - Actualizar documento</li>
                <li><code>DELETE /api/v1/documents/:id</code> - Eliminar documento</li>
                <li><code>GET /api/v1/tasks</code> - Listar tareas</li>
                <li><code>POST /api/v1/tasks</code> - Crear tarea</li>
                <li><code>PUT /api/v1/tasks/:id</code> - Actualizar tarea</li>
                <li><code>DELETE /api/v1/tasks/:id</code> - Eliminar tarea</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
